import { Form, Row } from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { ISubj, ISubjPrilozenesoubory } from 'types/amlz';

import ButtonComponent from 'components/atoms/ButtonComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import UploadComponent, { IFileList } from 'components/atoms/UploadComponent';
import TranslateText from 'components/hoc/TranslateText';

import { IcisStaty } from 'services/cisStaty';
import fileDownload from 'services/fileDownload';
import { prepareFileAmlz } from 'services/prepareFile';
import { EEsmStav, IOsobyData } from 'services/spravaEmitenta/C4osoby';

import { ETypSouboru } from 'enums/amlz/typSouboru';

import pscValidation from 'utils/validations/pscValidation';
import rodneCisloValidation from 'utils/validations/rodneCisloValidation';

interface IC4BPridatOsobu {
    personData: ISubj | undefined;
    setPersonData: (value: ISubj | undefined) => void;
    setSubmitPersonData: (value: ISubj | null) => void;
    countries?: IcisStaty[];
    subjIndex: number;
    osobyResp?: IOsobyData;
    idZadosti: number | string;
}

const C4BPridatOsobu: React.FC<IC4BPridatOsobu> = ({
    personData,
    setSubmitPersonData,
    setPersonData,
    countries,
    subjIndex,
    osobyResp,
    idZadosti
}) => {
    const [form] = Form.useForm();
    const setForceRerender = useState<boolean>(false)[1];
    const [uploading, setUploading] = useState<boolean>();
    const [loading, setLoading] = useState<boolean>(true);
    const [fileList, setFileList] = useState<IFileList[] | undefined>(
        personData?.prilozenesoubory?.map((e, i) => {
            return {
                uid: String(i),
                name: e.orignazev ? e.orignazev : 'bez nazvu',
                fileName: e.tempnazev ? e.tempnazev : 'a',
                type: e.orignazev ? e.orignazev.split('.').slice(-1)[0] : '',
                size: 0,
                status: 'done'
            };
        })
    );
    const [prilozeneSoubory, setPrilozeneSoubory] = useState<ISubjPrilozenesoubory[]>(
        personData?.prilozenesoubory || []
    );
    const currentOsobaESMdata =
        osobyResp?.osoby &&
        osobyResp.osoby.filter(e => e.jmeno === personData?.jmeno && e.prijmeni === personData.prijmeni)[0];

    const handleSelectChange = (name: string, value: string[] | string) => {
        if (typeof value === 'string') {
            form.setFieldsValue({
                [name]: value
            });
        } else {
            form.setFieldsValue({
                [name]: [...value]
            });
        }
        setForceRerender(val => !val);
    };

    useEffect(() => {
        setLoading(true);
        personData &&
            form.setFieldsValue({
                stat: personData.stat
            });
        setTimeout(() => setLoading(false), 0);
        window.scrollTo(0, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const uploadProps = {
        buttonLoading: uploading,
        showDownloadIcon: true,
        onChange: (data: UploadChangeParam) => {
            if (data.file.status === 'uploading' && setUploading) setUploading(true);
            if (data.file.status !== 'uploading' && setUploading) setUploading(false);
            if (data.fileList.length) {
                if (data.file.response) {
                    setPrilozeneSoubory(
                        data.fileList
                            .filter(f => f.status && f.status !== 'error')
                            .map(e => {
                                return {
                                    orignazev: e.name.normalize(),
                                    tempnazev: e.response ? e.response.tempnazev : e.fileName,
                                    oblast: ETypSouboru.SOUBOR_DOKLAD
                                };
                            })
                    );
                    form.validateFields();
                }

                setFileList(
                    data.fileList
                        .filter(f => f.status && f.status !== 'error')
                        .map(e => {
                            return {
                                uid: e.uid,
                                name: e.name.normalize(),
                                fileName: e.fileName ? e.fileName : e.response ? e.response.tempnazev : '',
                                type: e.type!,
                                size: e.size!,
                                status: e.status ? e.status : 'error'
                            };
                        })
                );
            }
        },
        onRemove: (file: UploadFile) => {
            if (fileList?.length) {
                const oldState = [...fileList];
                const newState = oldState.filter(e => e.uid !== file.uid);
                setFileList(newState);
                const newSoubory = [...prilozeneSoubory].filter(e => e.tempnazev !== file.fileName);
                setPrilozeneSoubory(newSoubory);
            }
        },
        onDownload: (file: UploadFile) => {
            setUploading(true);
            prepareFileAmlz(idZadosti, file.fileName ? file.fileName : '')
                .then(resp => {
                    fileDownload(resp.redirect, file.name ? file.name : 'file');
                })
                .finally(() => setUploading(false));
        },
        fileList: fileList,
        buttonDisabled: fileList ? fileList.length > 4 : false
    };

    // TODO
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (fieldsValue: any) => {
        const subjData = {
            ...fieldsValue,
            ...(fieldsValue.datumnar && {
                datumnar: fieldsValue.datumnar.format('YYYY-MM-DD')
            }),
            ...(fieldsValue.misto && {
                misto: fieldsValue.misto
            }),
            stavesm: subjIndex === -1 || personData?.stavesm === EEsmStav.RUCNE ? EEsmStav.RUCNE : EEsmStav.ESMOK,
            ...(prilozeneSoubory.length && {
                prilozenesoubory: prilozeneSoubory
            })
        };
        delete subjData.soubor;
        setSubmitPersonData(subjData);
    };

    return (
        <Row justify="start">
            {!loading && (
                <Form
                    preserve={false}
                    scrollToFirstError={true}
                    form={form}
                    layout="vertical"
                    initialValues={{
                        remember: true
                    }}
                    onFinish={onFinish}
                    name="C4BPridatOsobu"
                    style={{
                        width: '100%'
                    }}
                >
                    <Title level={4} text={'FORMULARE_AMLZ_C4B_TITLE'} />
                    <p>
                        <TranslateText>FORMULARE_AMLZ_C4_OSOBA_TEXT</TranslateText>
                    </p>
                    <>
                        <Title level={5} dividerTitle={true} text="OSOBA" />
                        <InputComponent
                            label="JMENO"
                            name="jmeno"
                            initialValue={personData?.jmeno}
                            type="text"
                            maxLength={100}
                            disabled={!!currentOsobaESMdata?.jmeno && !!currentOsobaESMdata?.prijmeni}
                        />
                        <InputComponent
                            label="PRIJMENI"
                            name="prijmeni"
                            initialValue={personData?.prijmeni}
                            type="text"
                            maxLength={100}
                            disabled={!!currentOsobaESMdata?.jmeno && !!currentOsobaESMdata?.prijmeni}
                        />
                        <SelectComponent
                            name="stat"
                            isFormItem={true}
                            labelParagraph="FORMULARE_ZEME_PUVODU"
                            defaultValue={personData?.stat}
                            onChange={value => handleSelectChange('stat', value)}
                            data={countries ? countries : []}
                        />
                        <InputComponent
                            label="FORMULARE_MISTO_NAROZENI"
                            name="misto"
                            initialValue={personData?.misto}
                            type="text"
                            maxLength={100}
                        />
                        {form.getFieldsValue(['stat']).stat === 'CZ' ? (
                            <InputComponent
                                label="FORMULARE_RODNE_CISLO_CR"
                                name="idmb"
                                initialValue={personData?.idmb}
                                type="text"
                                maxLength={10}
                                rules={rodneCisloValidation()}
                            />
                        ) : (
                            <DatePickerComponent
                                name="datumnar"
                                label="FORMULARE_DATUM_NAROZENI"
                                initialValue={
                                    personData?.datumnar
                                        ? dayjs(personData?.datumnar)
                                        : dayjs(currentOsobaESMdata?.datumnar)
                                }
                                disabledDate={current => current > dayjs()}
                                defaultPickerValue={
                                    personData?.datumnar
                                        ? dayjs(personData?.datumnar)
                                        : dayjs(currentOsobaESMdata?.datumnar)
                                }
                                defaultValue={
                                    personData?.datumnar
                                        ? dayjs(personData?.datumnar)
                                        : dayjs(currentOsobaESMdata?.datumnar)
                                }
                                disabled={!!currentOsobaESMdata?.datumnar}
                            />
                        )}
                        <Title level={5} dividerTitle={true} text="FORMULARE_ADRESA_POBYTU" />
                        <InputComponent
                            label="ULICE_CP"
                            name="adma"
                            initialValue={personData?.adma?.trim()}
                            type="text"
                            maxLength={50}
                            disabled={!!currentOsobaESMdata?.adma}
                            {...(currentOsobaESMdata?.adma
                                ? {
                                      rules: []
                                  }
                                : {})}
                        />
                        <InputComponent
                            label="MESTO"
                            name="mema"
                            initialValue={personData?.mema?.trim()}
                            type="text"
                            maxLength={50}
                            disabled={!!currentOsobaESMdata?.mema}
                            {...(currentOsobaESMdata?.mema
                                ? {
                                      rules: []
                                  }
                                : {})}
                        />{' '}
                        <SelectComponent
                            name="kost"
                            isFormItem={true}
                            labelParagraph="STAT"
                            defaultValue={personData?.kost?.trim()}
                            onChange={value => handleSelectChange('kost', value)}
                            data={countries ? countries : []}
                            disabled={!!currentOsobaESMdata?.kost}
                            {...(currentOsobaESMdata?.kost
                                ? {
                                      rules: []
                                  }
                                : {})}
                        />
                        <InputComponent
                            label="PSC"
                            name="pscm"
                            initialValue={personData?.pscm?.trim()}
                            type="text"
                            maxLength={50}
                            disabled={!!currentOsobaESMdata?.pscm}
                            rules={currentOsobaESMdata?.pscm ? [] : pscValidation('kost')}
                        />
                        {(subjIndex === -1 || personData?.stavesm === EEsmStav.RUCNE) && (
                            <Form.Item
                                style={{
                                    marginTop: 16,
                                    marginBottom: 16
                                }}
                                name="soubor"
                                label={<Paragraph text={'VLOZTE_DOKUMENT'} strong={true} />}
                            >
                                <div
                                    style={{
                                        marginBottom: 22
                                    }}
                                >
                                    <UploadComponent {...uploadProps} id="C4uploadBtn" />
                                </div>
                            </Form.Item>
                        )}
                    </>

                    <Row className="button-row">
                        <ButtonComponent
                            buttontext="ZPET"
                            id="C4ZpetBtn"
                            type="default"
                            onClick={() => {
                                setSubmitPersonData(null);
                                setPersonData(undefined);
                            }}
                        />
                        <ButtonComponent
                            buttontext="POKRACOVAT"
                            id="C4PokracovatBtn"
                            type="primary"
                            onClick={() => form.submit()}
                            loading={uploading}
                        />
                    </Row>
                </Form>
            )}
        </Row>
    );
};

export default C4BPridatOsobu;
